/**
 * Pure decision logic for the screen bus (signage rotation + foreground
 * takeover video): error recovery and single-item continuation. Mirrors the
 * audio bus' playback-failure-policy so a broken video skips ahead while a
 * fully-broken rotation halts (resync + backoff) instead of hot-looping.
 */

import type { FailureBudget } from "./playback-failure-policy";

export type ScreenSurface = "signage" | "foreground";

export type ScreenFailureAction = "advance" | "halt";

export interface ScreenFailureDecision {
  action: ScreenFailureAction;
  consecutiveFailures: number;
}

/**
 * Record a screen-bus media failure and decide recovery: `advance` the
 * rotation while a single bad item is a plausible cause; `halt` once every
 * candidate has failed inside the window — skipping has stopped being a
 * viable strategy, so the caller must resync and retry after a backoff
 * rather than storm through the (all-broken) rotation.
 */
export function recordScreenFailure(
  budget: FailureBudget,
  candidateCount: number,
  now = Date.now(),
): ScreenFailureDecision {
  const consecutiveFailures = budget.record(now);
  return {
    action: budget.tripped(candidateCount) ? "halt" : "advance",
    consecutiveFailures,
  };
}

/**
 * A signage rotation with exactly one video must loop on the element itself:
 * advancing lands on the same item object, React bails on the state update,
 * the element never remounts, and without `loop` the video plays once and
 * freezes on its last frame. Foreground takeovers never loop — they must end
 * so the screen returns to the signage bed.
 */
export function shouldLoopVideo(rotationLength: number): boolean {
  return rotationLength === 1;
}

/**
 * Whether attaching this element as the on-air signage layer must restart it.
 *
 * An on-air signage video may never sit on its last frame: that is a dead
 * screen the operator console still reports online. The crossfade keys its
 * layers by item id, so an item demoted to `outgoing` and re-promoted while it
 * is still inside the fade linger hands the element that just FINISHED back as
 * the current layer — React re-attaches the ref instead of remounting, so
 * `autoPlay` never runs again and `ended` has already fired. Every caller that
 * lands a finished item back on air (a boundary re-sync, a slot change into
 * the same item) restarts it, the same way `isSameItemAdvance` does for a
 * same-item advance.
 */
export function shouldReplayAttachedVideo(
  el: { ended: boolean } | null,
): boolean {
  return Boolean(el?.ended);
}

/**
 * Whether advancing re-applies the item already on screen (single-item
 * rotation, or duplicate adjacent entries). React bails on the identical
 * state, so the caller must restart the element imperatively
 * (currentTime = 0 + play()) instead of relying on a remount.
 */
export function isSameItemAdvance(
  currentItemId: string | null | undefined,
  nextItemId: string | null | undefined,
): boolean {
  return currentItemId != null && currentItemId === nextItemId;
}
